import { useRef, useEffect } from 'react';

const POLL_INTERVAL = 10000;    // check every 10s
const IDLE_BEFORE_SYNC = 120000; // but only sync after 2 min idle
// Pulls fresh data from server after 2min of inactivity
const useIdleSync = ({ pendingChangesRef, refreshSheet, view }) => {
  const hasSyncedRef = useRef(false);
  const idleSinceRef = useRef(null); // tracks when idle started

  useEffect(() => {
    const interval = setInterval(async () => {
      if (view || document.visibilityState !== 'visible') return;

      const current = pendingChangesRef.current;
      const hasUnsaved =
        current.created.size > 0 ||
        current.updated.size > 0 ||
        current.deleted.size > 0;

      if (hasUnsaved) {
        // user active — reset everything
        hasSyncedRef.current = false;
        idleSinceRef.current = null;
        return;
      }

      // start counting idle time
      if (!idleSinceRef.current) {
        idleSinceRef.current = Date.now();
        return;
      }

      const idleFor = Date.now() - idleSinceRef.current;

      // not been idle long enough yet
      if (idleFor < IDLE_BEFORE_SYNC) return;

      // already synced this idle session
      if (hasSyncedRef.current) return;

      // idle for 2 min and not synced yet — fire once
      hasSyncedRef.current = true;
      try {
        if (typeof refreshSheet === 'function') {
          await refreshSheet();
        }
      } catch (e) {
        console.warn('Background sync failed', e);
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [pendingChangesRef, refreshSheet]);
};

export default useIdleSync;